<!-- Email setup — this platform doesn't (and won't) host mailboxes itself;
     see backend/routes/emailSetup.js for why. This page just points a
     contractor at Google Workspace for a real inbox at their own domain,
     and offers a paid concierge option for contractors who'd rather have
     staff do the Workspace + DNS setup for them. The concierge option is a
     request queue, not a checkout — no payment happens on this page, it's
     collected/invoiced separately. -->
<script setup>
import { computed, onMounted, ref } from 'vue'
import { API_BASE_URL, company, getAuthToken } from '@/stores/appConfig'

const GOOGLE_WORKSPACE_URL = 'https://workspace.google.com/'

async function authHeaders() {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  }
}

// ── Existing request (if any) ───────────────────────────────────────────
const loading = ref(true)
const loadError = ref('')
const existingRequest = ref(null)

async function loadExistingRequest() {
  if (!company.company_id) { loading.value = false; return }
  loading.value = true
  loadError.value = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/email-setup-requests?company_id=${company.company_id}`, { headers })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.ok) throw new Error(data.error || `Unable to load request status (${res.status}).`)
    existingRequest.value = data.request
  } catch (err) {
    loadError.value = err.message || 'Unable to load request status.'
  } finally {
    loading.value = false
  }
}

onMounted(loadExistingRequest)

// ── Concierge request form ──────────────────────────────────────────────
const contactEmail = ref('')
const notes = ref('')
const submitting = ref(false)
const submitError = ref('')

const hasOpenRequest = computed(() =>
  existingRequest.value && ['new', 'in_progress'].includes(existingRequest.value.status)
)

async function submitRequest() {
  if (!company.company_id || submitting.value) return

  submitting.value = true
  submitError.value = ''

  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/email-setup-requests`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        company_id: company.company_id,
        contact_email: contactEmail.value || undefined,
        notes: notes.value || undefined,
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.ok) throw new Error(data.error || `Unable to submit request (${res.status}).`)
    existingRequest.value = data.request
  } catch (err) {
    submitError.value = err.message || 'Unable to submit request.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <main class="email-page">
    <section class="email-shell">
      <header class="page-header">
        <div>
          <p class="eyebrow">CMS</p>
          <h1>Email</h1>
        </div>
      </header>

      <section class="card">
        <h2>Get a professional inbox</h2>
        <p class="hint">
          A real inbox at your own domain (e.g. you@yourcompany.com) isn't something we host directly —
          it comes from Google Workspace or a similar provider, billed directly by them. Once you sign up,
          you'll need to point a couple of DNS records at what they give you — the concierge option below
          can take care of that part for you.
        </p>
        <a
          class="btn-primary"
          :href="GOOGLE_WORKSPACE_URL"
          target="_blank"
          rel="noopener"
        >
          Set up Google Workspace
        </a>
      </section>

      <section class="card">
        <h2>Want us to set it up for you? — $25</h2>
        <p class="hint">
          Our team will handle the Google Workspace setup and point your domain's DNS records at it for you.
          Submitting this doesn't charge you anything here — we'll follow up to collect payment and get started.
        </p>

        <div v-if="loading" class="hint">Loading…</div>

        <div v-else-if="hasOpenRequest" class="status-box">
          <v-icon size="18">mdi-check-circle-outline</v-icon>
          <div>
            <strong>Request submitted</strong>
            <p class="hint">
              We received your request on {{ new Date(existingRequest.created_at).toLocaleDateString() }} — status: {{ existingRequest.status.replace('_', ' ') }}.
              We'll be in touch.
            </p>
          </div>
        </div>

        <form v-else class="request-form" @submit.prevent="submitRequest">
          <label class="field">
            <span>Contact email (optional — defaults to your account email)</span>
            <input v-model="contactEmail" type="email" placeholder="you@yourcompany.com" />
          </label>
          <label class="field">
            <span>Anything we should know? (optional)</span>
            <textarea v-model="notes" rows="3" placeholder="e.g. preferred number of mailboxes, existing domain provider..." />
          </label>
          <p v-if="submitError" class="error">{{ submitError }}</p>
          <p v-if="loadError" class="error">{{ loadError }}</p>
          <button class="btn-primary" type="submit" :disabled="submitting">
            {{ submitting ? 'Submitting…' : 'Request setup help — $25' }}
          </button>
        </form>
      </section>
    </section>
  </main>
</template>

<style scoped>
/* Tokens mirrored from DomainView.vue/SettingsView.vue (8px radius,
   var(--surface)/var(--border-subtle) panels, #f26e2e primary, #1f7a63 teal
   eyebrow) so this sits inside the app rather than looking like a one-off. */
.email-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.email-shell {
  width: min(760px, 100%);
  margin: 0 auto;
}

.page-header {
  margin-bottom: 20px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.card {
  padding: 22px 24px;
  margin-bottom: 18px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 2px 8px rgba(23, 35, 31, 0.05);
}

.card h2 {
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 800;
}

.hint {
  margin: 0 0 16px;
  color: var(--text-secondary);
  font-size: 0.88rem;
  line-height: 1.55;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.request-form {
  display: grid;
  gap: 14px;
}

.field {
  display: grid;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.field input,
.field textarea {
  padding: 10px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.88rem;
  resize: vertical;
}

.error {
  margin: 0;
  color: #c0392b;
  font-size: 0.82rem;
}

.status-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.status-box strong {
  display: block;
  margin-bottom: 4px;
}

.status-box .hint {
  margin: 0;
}
</style>
